import { type WorkoutFormat } from "api-client/legacy-trpc";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  type InsertWorkoutItemProps,
  type ParentContext,
  getAllowedSectionOptions,
  getParentContext,
} from "src/workouts/AddExerciseOrBlock.shared";
import { ImportSectionDialog } from "src/workouts/ImportSectionDialog";
import {
  InsertButton,
  LinkUnlinkWorkoutItemsButton,
} from "src/workouts/LinkUnlinkWorkoutItemsButton";
import { useInsertWorkoutItem } from "src/workouts/useInsertWorkoutItem";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";

export const InsertWorkoutItem = observer(function InsertWorkoutItem(
  props: InsertWorkoutItemProps,
) {
  const insertWorkoutItem = useInsertWorkoutItem();
  const workoutEditStore = useWorkoutEditStore();
  const permissions = useWorkoutPermissions();
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const parentContext: ParentContext | null = props.parentId
    ? getParentContext(workoutEditStore.getWorkoutItemById(props.parentId))
    : null;

  const { sections } = getAllowedSectionOptions(parentContext);

  const isInsideSuperset = props.itemId
    ? workoutEditStore.isInsideSuperset(props.itemId)
    : false;

  function handleAddExercise() {
    insertWorkoutItem(props.index, props.parentId, "NORMAL");
  }

  function handleAddSection(format: WorkoutFormat) {
    insertWorkoutItem(props.index, props.parentId, "GROUP", {
      format: format,
      groupType: "WORKOUT",
    });
  }

  function handleImportFromLibrary() {
    setImportDialogOpen(true);
  }

  const importFromLibraryHandler = permissions.canImportFromLibrary
    ? handleImportFromLibrary
    : undefined;

  if (isInsideSuperset && props.previousItemId && props.itemId) {
    return (
      <div className="relative h-0 z-10">
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
          <LinkUnlinkWorkoutItemsButton
            itemId={props.itemId}
            previousItemId={props.previousItemId}
            onInsert={handleAddExercise}
            sections={sections}
            onAddSection={handleAddSection}
            onImportFromLibrary={importFromLibraryHandler}
          />
        </div>
        {permissions.canImportFromLibrary && (
          <ImportSectionDialog
            open={importDialogOpen}
            onOpenChange={setImportDialogOpen}
            insertAt={props.index}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      {props.previousItemId && props.itemId ? (
        <LinkUnlinkWorkoutItemsButton
          itemId={props.itemId}
          previousItemId={props.previousItemId}
          onInsert={handleAddExercise}
          sections={sections}
          onAddSection={handleAddSection}
          onImportFromLibrary={importFromLibraryHandler}
        />
      ) : (
        <InsertButton
          onInsert={handleAddExercise}
          sections={sections}
          onAddSection={handleAddSection}
          onImportFromLibrary={importFromLibraryHandler}
        />
      )}
      {permissions.canImportFromLibrary && (
        <ImportSectionDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          insertAt={props.index}
        />
      )}
    </div>
  );
});
